import type * as THREE from 'three/webgpu';
import type { SectionableModelRootRegistration } from '../../types/section-view/SectionViewTypes';

/**
 * 场景作用域到可剖切模型根的集中索引。
 *
 * 使用 WeakMap 让模型场景释放后索引自然失效，避免把剖面运行时状态写入 Object3D.userData。
 */
const sectionableModelRootRegistrationByScope = new WeakMap<THREE.Object3D, SectionableModelRootRegistration>();

/**
 * 注册场景作用域对应的可剖切模型根。
 *
 * @param scope 场景或稳定模型作用域。
 * @param registration 可剖切模型根及内容变化回调。
 * @returns 无返回值。
 * @throws 同一作用域已注册另一份运行时时抛出错误，禁止静默覆盖仍在使用的裁剪根。
 */
export function registerSectionableModelRoot(
    scope: THREE.Object3D,
    registration: SectionableModelRootRegistration,
): void {
    const existingRegistration = sectionableModelRootRegistrationByScope.get(scope);
    if (existingRegistration && existingRegistration !== registration) {
        throw new Error(`可剖切模型根重复注册: scope=${scope.uuid}`);
    }
    if (registration.root.isClippingGroup !== true) {
        throw new Error(`可剖切模型根必须是 THREE.ClippingGroup: scope=${scope.uuid}`);
    }

    sectionableModelRootRegistrationByScope.set(scope, registration);
}

/**
 * 注销指定作用域的可剖切模型根。
 *
 * @param scope 注册时使用的场景作用域。
 * @returns 无返回值。
 */
export function unregisterSectionableModelRoot(scope: THREE.Object3D): void {
    sectionableModelRootRegistrationByScope.delete(scope);
}

/**
 * 读取直接注册在指定作用域上的运行时记录。
 *
 * 该入口用于 dispose 等所有权敏感链路，避免子树误取祖先作用域后注销错误的模型根。
 *
 * @param scope 待查询的场景作用域。
 * @returns 直接注册记录；不存在时返回 null。
 */
export function resolveSectionableModelRootRegistrationAtScope(
    scope: THREE.Object3D | null | undefined,
): SectionableModelRootRegistration | null {
    return scope ? sectionableModelRootRegistrationByScope.get(scope) ?? null : null;
}

/**
 * 从任意场景对象沿父链解析所属的可剖切模型根记录。
 *
 * @param object 场景作用域或其任意后代对象。
 * @returns 最近的可剖切模型根记录；不存在时返回 null。
 */
export function resolveSectionableModelRootRegistration(
    object: THREE.Object3D | null | undefined,
): SectionableModelRootRegistration | null {
    let currentObject = object ?? null;
    while (currentObject) {
        const registration = sectionableModelRootRegistrationByScope.get(currentObject);
        if (registration) {
            return registration;
        }
        currentObject = currentObject.parent;
    }
    return null;
}

/**
 * 从任意场景对象解析所属的 WebGPU 局部裁剪根。
 *
 * @param object 场景作用域或其任意后代对象。
 * @returns 可剖切 ClippingGroup；不存在时返回 null。
 */
export function resolveSectionableModelRoot(
    object: THREE.Object3D | null | undefined,
): THREE.ClippingGroup | null {
    return resolveSectionableModelRootRegistration(object)?.root ?? null;
}

/**
 * 通知所属可剖切模型根，其模型内容或可见性已发生变化。
 *
 * 渲染控制器据此重算包围盒并重建共享源几何的 stencil 代理；调用方不需要接触渲染资源。
 *
 * @param object 场景作用域或其任意后代对象。
 * @returns 找到并通知运行时时返回 true，否则返回 false。
 */
export function notifySectionableModelRootContentChanged(
    object: THREE.Object3D | null | undefined,
): boolean {
    const registration = resolveSectionableModelRootRegistration(object);
    if (!registration) {
        return false;
    }

    registration.onContentChanged();
    return true;
}
